import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PromptStep from './PromptStep';
import { useAuth } from '../context/AuthContext';

// Rebuilt 2026-07-16: sso-add-email flow now only has the prompt stage
// (writes attributes.pending_email, nothing else — see accountUpdate.js
// header comment for the full reasoning). The backend sends its own
// confirmation email and reports back { done: true, email }. The
// account's real email only changes once the person clicks that link
// (see ConfirmEmail.jsx / /api/account/confirm-email). This replaces the
// old design, which wrote to the real email field immediately and relied
// on Authentik's Email/Consent stages — which reliably crashed on
// ak-stage-access-denied (see chat history 2026-07-14/15), now moot since
// those stages are no longer used here.

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

// Only these domains are accepted for a BESCOM account email — catches
// typo'd TLDs (e.g. @gmail.co) and any domain we haven't decided to trust,
// before it ever reaches Authentik.
const ALLOWED_EMAIL_DOMAINS = [
  'bescom.co.in',
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  'hotmail.com',
  'rediffmail.com',
];

function isAllowedEmailDomain(email) {
  const domain = String(email || '').split('@')[1]?.toLowerCase().trim();
  return !!domain && ALLOWED_EMAIL_DOMAINS.includes(domain);
}

const page = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

const card = {
  background: 'rgba(255, 255, 255, 0.65)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  borderRadius: '16px',
  padding: '48px 40px',
  width: '100%',
  maxWidth: '420px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
};

const btn = {
  width: '100%',
  padding: '14px',
  background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  marginTop: '8px',
};

const btnSecondary = {
  width: '100%',
  padding: '12px',
  background: 'none',
  border: '1px solid #D3D1C7',
  color: '#666',
  borderRadius: '10px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  marginTop: '10px',
};

const btnDisabled = { ...btn, opacity: 0.6, cursor: 'not-allowed' };

export default function AddEmail() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [values, setValues] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // Phase 2 of the httpOnly-cookie migration (see AuthContext.js's own
  // header comment) — no more synchronous "is there a token" pre-check
  // possible (an httpOnly cookie can't be read directly). Every request
  // below already has its own 401 handling that redirects to '/' — that
  // now does the whole job on its own, including the "no session at all"
  // case this pre-check used to shortcut.
  useEffect(() => {
    fetchChallenge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchChallenge() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/add-email/challenge`, {
        credentials: 'include',
      });
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      const data = await res.json();
      setChallenge(data);
      setValues({});
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setLoading(false);
    }
  }

  async function submitStep(body) {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/add-email/step`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }

      if (data.done) {
        setPendingEmail(data.email || values.email);
        return;
      }

      if (data.errors) {
        const firstKey = Object.keys(data.errors)[0];
        setError(data.errors[firstKey]?.[0]?.string || 'Something went wrong. Please try again.');
      }

      setChallenge(data);
      setValues({});
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setLoading(false);
    }
  }

  function handleCancelPrompt() {
    navigate('/dashboard');
  }

  // pendingEmail means attributes.pending_email is already written on the
  // account and a confirmation link has already been sent (see
  // accountUpdate.js) — a real cancel has to undo both, not just leave
  // the page, or the address would stay pending forever / the old link
  // would still work.
  async function handleCancelPending() {
    setCancelling(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/add-email/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Could not cancel. Please try again.');
        return;
      }
      navigate('/dashboard');
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div style={page}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/bescom-logo-ui.png" alt="BESCOM" style={{ width: '80px', marginBottom: '16px' }} />
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px' }}>
            Add Email Address
          </h1>
          {!pendingEmail && (
            <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
              {challenge?.title || 'Loading…'}
            </p>
          )}
        </div>

        {user && !pendingEmail && (
          <div style={{
            background: '#F0F4F8', borderRadius: '8px', padding: '10px 14px',
            marginBottom: '20px', textAlign: 'center',
          }}>
            <p style={{ margin: 0, fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Adding an email for
            </p>
            <p style={{ margin: '2px 0 0', fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
              {user.profile?.preferred_username || user.profile?.name}
            </p>
            {user.profile?.email && (
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#666' }}>{user.profile.email}</p>
            )}
          </div>
        )}

        {error && (
          <div style={{
            background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
            borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        {pendingEmail ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>📧</div>
            <p style={{ color: '#1a1a1a', fontWeight: '600', marginBottom: '8px' }}>
              Check your inbox
            </p>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
              We've sent a confirmation link to <strong>{pendingEmail}</strong>. Click
              it to finish adding this email address — it won't appear on your
              profile until you do.
            </p>
            <button onClick={() => navigate('/dashboard')} style={btn}>
              Back to Dashboard
            </button>
            <button onClick={handleCancelPending} disabled={cancelling} style={cancelling ? btnDisabled : btnSecondary}>
              {cancelling ? 'Cancelling…' : "Changed your mind? Cancel this request"}
            </button>
          </div>
        ) : (
          <>
            {loading && !challenge && (
              <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>Loading…</p>
            )}

            {challenge?.stage === 'ak-stage-prompt' && (
              <>
                <PromptStep
                  fields={challenge.raw.fields}
                  values={values}
                  setValues={setValues}
                  loading={loading}
                  onSubmit={() => {
                    if (!isAllowedEmailDomain(values.email)) {
                      setError(
                        `Please use an email ending in one of: ${ALLOWED_EMAIL_DOMAINS.join(', ')}`
                      );
                      return;
                    }
                    submitStep(values);
                  }}
                />
                <button onClick={handleCancelPrompt} style={btnSecondary}>
                  Cancel
                </button>
              </>
            )}

            {challenge && challenge.stage !== 'ak-stage-prompt' && (
              <p style={{ textAlign: 'center', color: '#666' }}>Unhandled stage: {challenge.stage}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
